import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

interface SEOHeadProps {
  titleKey?: string;
  descriptionKey?: string;
  keywordsKey?: string;
}

export default function SEOHead({ titleKey, descriptionKey, keywordsKey }: SEOHeadProps = {}) {
  const { language, t } = useLanguage();
  const location = useLocation();
  
  // Determine page-specific keys based on route if not provided
  const getPageKeys = () => {
    if (titleKey && descriptionKey) {
      return { title: titleKey, description: descriptionKey, keywords: keywordsKey || 'meta.keywords' };
    }
    
    const path = location.pathname;
    if (path.includes('/about/company')) {
      return { title: 'company.title', description: 'company.intro.desc', keywords: 'meta.keywords' };
    } else if (path.includes('/about/mission-vision')) {
      return { title: 'mission.title', description: 'mission.our.desc', keywords: 'meta.keywords' };
    } else if (path.includes('/about/career')) {
      return { title: 'career.title', description: 'career.intro.desc', keywords: 'meta.keywords' };
    }
    return { title: 'meta.title', description: 'meta.description', keywords: 'meta.keywords' };
  };
  
  const pageKeys = getPageKeys();

  useEffect(() => {
    // Update document title
    const pageTitle = t(pageKeys.title);
    document.title = pageKeys.title === 'meta.title' ? pageTitle : `${pageTitle} - Teknolobi`;

    // Update or create meta description
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', t(pageKeys.description));

    // Update or create meta keywords
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.setAttribute('name', 'keywords');
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.setAttribute('content', t(pageKeys.keywords));

    // Update html lang attribute
    document.documentElement.lang = language;

    // Update or create Open Graph meta tags
    const ogTags = [
      { property: 'og:title', content: document.title },
      { property: 'og:description', content: t(pageKeys.description) },
      { property: 'og:type', content: 'website' },
      { property: 'og:locale', content: language === 'tr' ? 'tr_TR' : 'en_US' },
      { property: 'og:site_name', content: 'Teknolobi' },
    ];

    ogTags.forEach(({ property, content }) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    });

    // Update or create Twitter Card meta tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: document.title },
      { name: 'twitter:description', content: t(pageKeys.description) },
    ];

    twitterTags.forEach(({ name, content }) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('name', name);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    });

    // Add canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', window.location.href);

    // Add alternate language links
    let alternateTr = document.querySelector('link[hreflang="tr"]');
    if (!alternateTr) {
      alternateTr = document.createElement('link');
      alternateTr.setAttribute('rel', 'alternate');
      alternateTr.setAttribute('hreflang', 'tr');
      document.head.appendChild(alternateTr);
    }
    alternateTr.setAttribute('href', window.location.origin + '?lang=tr');

    let alternateEn = document.querySelector('link[hreflang="en"]');
    if (!alternateEn) {
      alternateEn = document.createElement('link');
      alternateEn.setAttribute('rel', 'alternate');
      alternateEn.setAttribute('hreflang', 'en');
      document.head.appendChild(alternateEn);
    }
    alternateEn.setAttribute('href', window.location.origin + '?lang=en');

  }, [language, t, location.pathname, pageKeys.title, pageKeys.description, pageKeys.keywords]);

  return null;
}